import { useLocation } from 'react-router';

import { SearchResultsBody } from '#/modules/search/components/Body/SearchResultsBody';
import { SearchResultsHeader } from '#/modules/search/components/Results/Header/SearchResultsHeader';
import { SearchResultsHits } from '#/modules/search/components/Results/Hits/SearchResultsHits';
import {
  useIsSearching,
  useSearchCurrentPageResults,
  useSearchLocation,
  useSearchSort,
} from '#/modules/search/contexts/SearchContext';
import { useSaveSearch } from '#/modules/search/hooks/useSaveSearch';
import { useSearchAnalytics } from '#/modules/search/hooks/useSearchAnalytics';
import { useSearchPageActions } from '#/modules/search/hooks/useSearchPageActions';
import type { SearchFacet } from '#/modules/search/types/SearchFacet';
import type { SearchType } from '#/modules/search/types/SearchType';
import {
  useSearchSavedListingsStore,
  useSearchSavedListingsSyncAlgoliaHits,
} from '#/modules/search/zustand-stores/searchSavedListingsStore';

import type { SearchPageLocationState } from './types/SearchPageLocationState';
import { useSearchPageLifecycle } from './useSearchPageLifecycle';
import { useSearchPageScrollEffect } from './useSearchPageScrollEffect';

type Props = {
  searchType: SearchType;
  searchFacets: SearchFacet[];
  showSortOptions: boolean;
};

export function SearchPage({
  searchType,
  searchFacets,
  showSortOptions,
}: Props) {
  const location = useLocation<SearchPageLocationState>();
  const scrollToId = location.state?.scrollToId;

  const { saveSearch } = useSaveSearch();
  const { setSearchUrlPageIndex, setSearchUrlSort } = useSearchPageActions({
    isInSearchLandingPage: false,
  });
  const isSearching = useIsSearching();
  const searchLocation = useSearchLocation();
  const sort = useSearchSort();

  const { getSearchTrackingData } = useSearchAnalytics();

  const { initialized } = useSearchPageLifecycle({
    searchType,
    getSearchTrackingData,
    searchFacets,
  });

  const currentResult = useSearchCurrentPageResults();
  const results = initialized ? currentResult : undefined;

  const { searchSavedListingsStore } = useSearchSavedListingsStore();
  useSearchSavedListingsSyncAlgoliaHits({
    hits: results?.hits,
    searchSavedListingsStore,
  });

  useSearchPageScrollEffect({ scrollToId, results });

  return (
    <SearchResultsBody>
      <SearchResultsHeader
        results={results}
        isSearching={isSearching}
        showSortOptions={showSortOptions}
        searchType={searchType}
        sort={sort}
        saveSearch={saveSearch}
        onChangeSort={setSearchUrlSort}
      />

      <SearchResultsHits
        results={results}
        isSearching={isSearching}
        searchType={searchType}
        searchLocation={searchLocation}
        saveSearch={saveSearch}
        getSearchTrackingData={getSearchTrackingData}
        onChangePageIndex={setSearchUrlPageIndex}
        searchSavedListingsStore={searchSavedListingsStore}
      />
    </SearchResultsBody>
  );
}
